import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { FileBarChart, FileSpreadsheet, FileText, FileDown, Loader2, ArrowLeft } from "lucide-react";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function CardReports() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/card-control/reports/summary?month=${month}`); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(`/card-control/reports/export?fmt=${fmt}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `card-report-${month}.${fmt === "xlsx" ? "xlsx" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setBusy(""); }
  };

  const t = data?.totals;
  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="card-reports-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <Link to="/finance/cards" className="text-[12px] text-slate-500 flex items-center gap-1 mb-1"><ArrowLeft className="h-3.5 w-3.5" /> Back to Card Control</Link>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2"><FileBarChart className="h-6 w-6 text-indigo-700" /> Card Reports</h1>
          <p className="text-sm text-slate-500">Monthly card spend, outstanding, payments, dues, rewards and EMI — exportable for your accountant.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-[12px]">Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="card-reports-month" /></div>
          <Button variant="outline" onClick={() => download("xlsx")} disabled={busy} data-testid="card-reports-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={busy} data-testid="card-reports-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={() => download("csv")} disabled={busy} data-testid="card-reports-csv"><FileDown className="h-4 w-4 mr-1" />CSV</Button>
        </div>
      </div>

      {loading && <div className="py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

      {t && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-5" data-testid="card-reports-totals">
            {[["Total spend", money(t.total_spend)], ["Business", money(t.spend_business)], ["Personal", money(t.spend_personal)],
              ["Payments", money(t.total_payments)], ["Outstanding", money(t.total_outstanding)], ["Due amount", money(t.due_amount)],
              ["Total limit", money(t.total_limit)], ["Rewards earned", t.rewards_earned], ["Rewards redeemed", t.rewards_redeemed], ["Cards", t.cards]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="text-[9px] uppercase tracking-wider text-slate-400">{l}</div>
                <div className="text-base font-semibold text-slate-800">{v}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <Section title="Per card">
              <Tbl headers={["Card", "Limit", "Outstanding", "Util %", "Spend"]}
                rows={data.cards.map((c) => [c.card_name, money(c.credit_limit), money(c.outstanding), `${c.utilisation_pct}%`, money(c.period_spend)])} empty="No cards" />
            </Section>
            <Section title="Spend by category">
              <Tbl headers={["Category", "Amount"]} rows={data.by_category.map((r) => [r.category, money(r.amount)])} empty="No spend this month" />
            </Section>
            <Section title="Payments made">
              <Tbl headers={["Voucher", "Card", "Date", "Amount"]} rows={data.payments.map((p) => [p.number, p.card_name, p.payment_date, money(p.amount)])} empty="No payments" />
            </Section>
            <Section title="Upcoming dues">
              <Tbl headers={["Card", "Due date", "Outstanding", "Status"]} rows={data.dues.map((d) => [d.card_name, d.due_date, money(d.outstanding), d.status])} empty="Nothing due" />
            </Section>
            <Section title="EMI schedule (active)">
              <Tbl headers={["Card", "Merchant", "Monthly", "Tenure", "End"]} rows={data.emis.map((e) => [e.card_name, e.merchant, money(e.monthly_amount), `${e.tenure_months}m`, e.end_date || "—"])} empty="No active EMI" />
            </Section>
            <Section title="Rewards activity">
              <Tbl headers={["Date", "Type", "Event", "Qty"]} rows={data.rewards.map((r) => [r.date, r.reward_type, r.event, r.quantity])} empty="No reward events" />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-3 py-2 text-[13px] font-semibold border-b border-slate-200">{title}</div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function Tbl({ headers, rows, empty }) {
  return (
    <table className="w-full text-[12px]">
      <thead className="bg-white text-slate-400 uppercase text-[9px] tracking-wider">
        <tr>{headers.map((h) => <th key={h} className="px-3 py-1.5 text-left whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <tr key={i} className="hover:bg-slate-50">{r.map((c, j) => <td key={j} className="px-3 py-1.5 whitespace-nowrap">{c}</td>)}</tr>
        ))}
        {!rows.length && <tr><td colSpan={headers.length} className="px-3 py-6 text-center text-slate-400">{empty}</td></tr>}
      </tbody>
    </table>
  );
}
